import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';

import { TimeRangeContext } from '../../../context/TimeRangeContext';
import SelfMonitorApi from '../../../api/SelfMonitorApi';
import { SelfMonitorData, SelfMonitorAgent, SelfMonitorSample } from '../../../types/SelfMonitor';

import StatTiles from './StatTiles';
import SelfMonitorChart, { SelfMonitorSeries } from './SelfMonitorChart';
import IngestTotals from './IngestTotals';
import { buildIngestIntervalSeries, buildIngestSeries, buildIngestTotalSeries, buildIngestTotals } from './ingest';
import { extraNum } from './sampleUtils';

interface OverviewTabProps {
    refreshKey: number;
}

// The archiver only retains this many minutes of samples - requesting more
// than this from the period selector does not get you more history, it just
// gets you everything currently held.
const MAX_BUFFER_MINUTES = 60;

// A small, distinguishable categorical palette - one colour per JVM/agent.
const PALETTE = ['#4C78A8', '#F58518', '#54A24B', '#B279A2', '#E45756', '#72B7B2'];
const SYSTEM_LOAD_COLOR = '#E87500';

const darkerShade = (hex: string): string => {
    const value = hex.replace('#', '');
    const channels = [0, 2, 4].map(offset => Math.round(parseInt(value.slice(offset, offset + 2), 16) * 0.68));
    return `#${channels.map(channel => channel.toString(16).padStart(2, '0')).join('')}`;
};

const bytesToMb = (bytes: number) => bytes / (1024 * 1024);

const toSeries = (samples: SelfMonitorSample[], accessor: (s: SelfMonitorSample) => number): [number, number][] =>
    samples.map(s => [s.time, accessor(s)]);

interface JvmGroup {
    key: string;
    label: string;
    samples: SelfMonitorSample[];
}

// Groups the archiver's own samples with any agent that shares its jvmid
// (a co-located agent) so a shared heap/CPU is shown once, not twice.
const buildJvmGroups = (data: SelfMonitorData): JvmGroup[] => {
    const groups: JvmGroup[] = [{
        key: data.archiver.jvmid || 'archiver',
        label: 'Archiver',
        samples: data.archiver.samples,
    }];

    data.agents.forEach(agent => {
        if (agent.colocated) {
            const existing = groups.find(g => g.key === (agent.jvmid || data.archiver.jvmid));
            if (existing) {
                existing.label += ` + Agent: ${agent.name}`;
            }
            return;
        }

        groups.push({
            key: agent.jvmid || `agent-${agent.id}`,
            label: `Agent: ${agent.name}`,
            samples: agent.samples,
        });
    });

    return groups;
};

const OverviewTab: React.FC<OverviewTabProps> = ({ refreshKey }) => {
    const { timeRange } = useContext(TimeRangeContext);
    const [data, setData] = useState<SelfMonitorData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [ingestMode, setIngestMode] = useState<'rate' | 'total' | 'interval'>('rate');

    const requestedMinutes = useMemo(() => {
        const minutes = Math.ceil((timeRange.to.valueOf() - timeRange.from.valueOf()) / 60000);
        return Math.max(1, minutes);
    }, [timeRange.from, timeRange.to]);

    const minutes = Math.min(requestedMinutes, MAX_BUFFER_MINUTES);
    const clamped = requestedMinutes > MAX_BUFFER_MINUTES;

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const result = await SelfMonitorApi.getSelfMonitor(minutes);
            setData(result);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [minutes]);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchData, timeRange.from.valueOf(), timeRange.to.valueOf(), refreshKey]);

    const jvmGroups = useMemo(() => (data ? buildJvmGroups(data) : []), [data]);

    const memorySeries: SelfMonitorSeries[] = useMemo(() => jvmGroups.flatMap((group, i) => {
        const color = PALETTE[i % PALETTE.length];
        return [
            { name: `${group.label} - used`, data: toSeries(group.samples, s => bytesToMb(s.heapused)), color },
            { name: `${group.label} - max`, data: toSeries(group.samples, s => bytesToMb(s.heapmax)), color, dashStyle: 'ShortDash' as const },
        ];
    }), [jvmGroups]);

    const cpuSeries: SelfMonitorSeries[] = useMemo(() => jvmGroups.flatMap((group, i) => {
        const color = PALETTE[i % PALETTE.length];
        const series: SelfMonitorSeries[] = [
            { name: `${group.label} - process CPU`, data: toSeries(group.samples, s => s.processcpu >= 0 ? s.processcpu * 100 : 0), color },
        ];
        const withLoad = group.samples.filter(s => s.systemload >= 0 && s.cores > 0);
        if (withLoad.length > 0) {
            series.push({
                name: `${group.label} - host load (% of cores)`,
                data: toSeries(withLoad, s => (s.systemload / s.cores) * 100),
                color: SYSTEM_LOAD_COLOR,
                dashStyle: 'ShortDash' as const,
            });
        }
        return series;
    }), [jvmGroups]);

    const threadSeries: SelfMonitorSeries[] = useMemo(() => {
        const series: SelfMonitorSeries[] = [];
        const archiverSamples = data?.archiver.samples ?? [];

        if (archiverSamples.length > 0) {
            series.push({
                name: 'HTTP request threads (shared, busy)',
                data: toSeries(archiverSamples, s => extraNum(s, 'http_threads_busy')),
                color: PALETTE[0],
            });
            series.push({
                name: 'Archiver scheduler (active)',
                data: toSeries(archiverSamples, s => extraNum(s, 'archiver_scheduler_active')),
                color: PALETTE[1],
            });
        }

        (data?.agents ?? []).forEach((agent: SelfMonitorAgent, i) => {
            series.push({
                name: `Agent: ${agent.name} - sensor executors (active)`,
                data: toSeries(agent.samples, s =>
                    extraNum(s, 'agent_activity_active') +
                    extraNum(s, 'agent_batch_active') +
                    extraNum(s, 'agent_host_active')),
                color: PALETTE[(i + 2) % PALETTE.length],
            });
        });

        return series;
    }, [data]);

    const threadPlotLines = useMemo(() => {
        const archiverSamples = data?.archiver.samples ?? [];
        const last = archiverSamples[archiverSamples.length - 1];
        const max = last ? extraNum(last, 'http_threads_max') : 0;
        return max > 0 ? [{ value: max, label: 'HTTP threads limit' }] : [];
    }, [data]);

    // agent_*_active is point-in-time (see AgentSelfMonitor.addExecutorStats) -
    // a sensor task usually runs for a few milliseconds once per interval, so
    // an instantaneous sample will almost always read 0 even while sensors
    // are firing on schedule. agent_*_completed is a per-sample delta of a
    // monotonic counter, so it proves recent activity the active count can't.
    const agentTasksCompleted = useMemo(() => (data?.agents ?? []).reduce((sum, agent) => {
        const last = agent.samples[agent.samples.length - 1];
        if (!last) return sum;
        return sum
            + extraNum(last, 'agent_activity_completed')
            + extraNum(last, 'agent_batch_completed')
            + extraNum(last, 'agent_host_completed');
    }, 0), [data]);

    const connectionSeries: SelfMonitorSeries[] = useMemo(() => {
        const series: SelfMonitorSeries[] = [];
        const archiverSamples = data?.archiver.samples ?? [];

        if (archiverSamples.length > 0) {
            series.push({
                name: 'Archiver -> Repository (active)',
                data: toSeries(archiverSamples, s => extraNum(s, 'archiver_pool_active')),
                color: PALETTE[0],
            });
            series.push({
                name: 'Archiver -> Repository (idle)',
                data: toSeries(archiverSamples, s => extraNum(s, 'archiver_pool_idle')),
                color: darkerShade(PALETTE[0]),
                dashStyle: 'ShortDash' as const,
            });
        }

        (data?.agents ?? []).forEach((agent: SelfMonitorAgent, i) => {
            const color = PALETTE[(i + 1) % PALETTE.length];
            series.push({
                name: `Agent: ${agent.name} -> Monitored targets (active)`,
                data: toSeries(agent.samples, s => extraNum(s, 'agent_pool_active')),
                color,
            });
            series.push({
                name: `Agent: ${agent.name} -> Monitored targets (idle)`,
                data: toSeries(agent.samples, s => extraNum(s, 'agent_pool_idle')),
                color: darkerShade(color),
                dashStyle: 'ShortDash' as const,
            });
        });

        return series;
    }, [data]);

    const connectionPlotLines = useMemo(() => {
        const lines: { value: number; label: string; color?: string }[] = [];
        const archiverSamples = data?.archiver.samples ?? [];
        const lastArchiver = archiverSamples[archiverSamples.length - 1];
        const archiverMax = lastArchiver ? extraNum(lastArchiver, 'archiver_pool_max') : 0;

        if (archiverMax > 0) {
            lines.push({ value: archiverMax, label: 'Repository pool limit', color: PALETTE[0] });
        }

        const agentMax = (data?.agents ?? []).reduce((sum, agent) => {
            const last = agent.samples[agent.samples.length - 1];
            return sum + (last ? extraNum(last, 'agent_pool_max') : 0);
        }, 0);

        if (agentMax > 0) {
            lines.push({ value: agentMax, label: 'Agent target pool limit (combined)', color: PALETTE[1] });
        }

        return lines;
    }, [data]);

    const ingestSamples = data?.archiver.samples ?? [];
    const ingestPeriodStart = Math.max(timeRange.from.valueOf(), timeRange.to.valueOf() - minutes * 60000);
    const ingestRateChart = useMemo(() => buildIngestSeries(ingestSamples, PALETTE), [ingestSamples]);
    const ingestTotalChart = useMemo(() => buildIngestTotalSeries(ingestSamples, ingestPeriodStart, PALETTE),
        [ingestSamples, ingestPeriodStart]);
    const ingestIntervalChart = useMemo(() => buildIngestIntervalSeries(ingestSamples, ingestPeriodStart, PALETTE),
        [ingestSamples, ingestPeriodStart]);
    const ingest = ingestMode === 'rate' ? ingestRateChart : ingestMode === 'total' ? ingestTotalChart : ingestIntervalChart;
    const ingestTotals = useMemo(() => buildIngestTotals(
        ingestSamples,
        ingestPeriodStart,
    ), [ingestSamples, ingestPeriodStart]);
    const ingestPeriodLabel = clamped ? `Last ${MAX_BUFFER_MINUTES} min` : `Selected period`;

    return (
        <div className="self-monitor-overview">
            <StatTiles data={data} periodMs={minutes * 60000} />

            <div className="self-monitor-tiles-spacer" />

            {clamped && (
                <div className="self-monitor-buffer-note text-muted">
                    Live buffer covers the last {MAX_BUFFER_MINUTES} minutes. Widen periods show only what is still retained.
                </div>
            )}

            <div className="row row-cols-lg-2 row-cols-1 self-monitor-overview__charts">
                <div className="col">
                    <SelfMonitorChart
                        title="JVM Memory"
                        icon="fa-memory"
                        series={memorySeries}
                        yAxisLabel="MB"
                        valueSuffix="MB"
                        loading={loading}
                    />
                </div>
                <div className="col">
                    <SelfMonitorChart
                        title="CPU Usage"
                        icon="fa-microchip"
                        series={cpuSeries}
                        yAxisLabel="%"
                        valueSuffix="%"
                        note="Process CPU is for the JVM. Host load is system-wide, normalized by the cores available to the JVM."
                        loading={loading}
                    />
                </div>
                <div className="col">
                    <SelfMonitorChart
                        title="Thread Count"
                        icon="fa-diagram-project"
                        series={threadSeries}
                        yAxisLabel="Threads"
                        plotLines={threadPlotLines}
                        note={agentTasksCompleted > 0
                            ? `${agentTasksCompleted} agent sensor task${agentTasksCompleted === 1 ? '' : 's'} completed recently - sensor executors run too briefly to usually be caught active.`
                            : undefined}
                        loading={loading}
                    />
                </div>
                <div className="col">
                    <SelfMonitorChart
                        title="DB Connections"
                        icon="fa-network-wired"
                        series={connectionSeries}
                        yAxisLabel="Connections"
                        plotLines={connectionPlotLines}
                        loading={loading}
                    />
                </div>
                <div className="col col-lg-9">
                    <SelfMonitorChart
                        title="Data Ingest"
                        icon="fa-cloud-upload-alt"
                        series={ingest.series}
                        yAxisLabel={ingest.unit}
                        valueSuffix={ingestMode === 'interval' ? 'MB' : ingest.unit}
                        note={ingestMode === 'rate'
                            ? 'Rate of data received from agents, by data type, averaged over each sample interval.'
                            : ingestMode === 'total'
                                ? 'Cumulative data received from agents by data type during the selected period.'
                                : 'Data received during each sample interval, stacked by data type.'}
                        headerActions={(
                            <div className="btn-group btn-group-sm" role="group" aria-label="Data ingest chart mode">
                                <button type="button" className={`btn ${ingestMode === 'rate' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    aria-pressed={ingestMode === 'rate'} onClick={() => setIngestMode('rate')}>Rate</button>
                                <button type="button" className={`btn ${ingestMode === 'total' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    aria-pressed={ingestMode === 'total'} onClick={() => setIngestMode('total')}>Total</button>
                                <button type="button" className={`btn ${ingestMode === 'interval' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    aria-pressed={ingestMode === 'interval'} onClick={() => setIngestMode('interval')}>Interval</button>
                            </div>
                        )}
                        loading={loading}
                    />
                </div>
                <div className="col col-lg-3">
                    <IngestTotals totals={ingestTotals} periodLabel={ingestPeriodLabel} palette={PALETTE} />
                </div>
            </div>
        </div>
    );
};

export default OverviewTab;
